import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Horizontal text ticker that loops a list of words/phrases continuously.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling track element.
 * @property {HTMLElement} [viewport] - The clipping viewport around the track.
 *
 * @extends Component<Refs>
 */
class HorizontalTextScroll extends Component {
  requiredRefs = ['track'];

  /** @type {Set<HorizontalTextScroll>} */
  static #fillQueue = new Set();

  /** @type {boolean} */
  static #fillQueued = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  #building = false;
  #widthRetries = 0;
  #lastSetWidth = 0;
  /** @type {number} */
  #viewportWidthHint = 0;

  get #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#scheduleFill();

    this.#resizeObserver = new ResizeObserver(this.#onResize);
    const viewport = this.refs.viewport ?? this;
    this.#resizeObserver.observe(viewport);

    const { track } = this.refs;
    if (track) {
      this.#mutationObserver = new MutationObserver(this.#onMutate);
      this.#mutationObserver.observe(track, {
        childList: true,
        subtree: true,
        // Live text editing in the theme editor fires characterData on every
        // keystroke. Rebuilding clones from that keeps the preview mutating,
        // which can stall the editor's loading overlay.
        characterData: !this.#isDesignMode,
      });
    }

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#scheduleFill();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#onResize.cancel();
    this.#onMutate.cancel();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleFill();
  }

  #onResize = debounce((entries) => {
    if (!this.isConnected) return;
    const width = entries?.[0]?.contentRect?.width;
    if (typeof width === 'number' && width > 0) this.#viewportWidthHint = width;
    this.#scheduleFill();
  }, 100);

  #onMutate = debounce(() => {
    if (!this.isConnected || this.#building) return;
    this.#scheduleFill();
  }, 100);

  #scheduleFill() {
    HorizontalTextScroll.#fillQueue.add(this);
    if (HorizontalTextScroll.#fillQueued) return;
    HorizontalTextScroll.#fillQueued = true;
    requestAnimationFrame(() => HorizontalTextScroll.#flushFills());
  }

  static #flushFills() {
    this.#fillQueued = false;
    const instances = [...this.#fillQueue];
    this.#fillQueue.clear();

    /** @type {{ instance: HorizontalTextScroll, originals: HTMLElement[], setWidth: number, viewportWidth: number, track: HTMLElement }[]} */
    const plans = [];

    for (const instance of instances) {
      if (!instance.isConnected) continue;
      const plan = instance.#collectFillPlan();
      if (plan) plans.push(plan);
    }

    for (const plan of plans) {
      plan.instance.#applyFillPlan(plan);
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getOriginals() {
    const { track } = this.refs;
    if (!track) return [];

    return /** @type {HTMLElement[]} */ (
      [...track.children].filter((node) => node instanceof HTMLElement && !node.hasAttribute('data-hts-clone'))
    );
  }

  /**
   * @param {Element} item
   * @returns {HTMLElement}
   */
  #cloneItem(item) {
    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
    clone.setAttribute('data-hts-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('data-shopify-editor-block');
    clone.removeAttribute('ref');
    return clone;
  }

  /**
   * @param {HTMLElement[]} originals
   * @returns {number}
   */
  #measureSetWidth(originals) {
    if (originals.length === 0) return 0;

    const gap = parseFloat(this.style.getPropertyValue('--hts-gap')) || 0;
    let width = 0;

    originals.forEach((item) => {
      width += item.offsetWidth;
    });

    return width + gap * originals.length;
  }

  #restartAnimation() {
    const { track } = this.refs;
    if (!track) return;

    track.style.animation = 'none';
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      track.style.animation = '';
    });
  }

  /**
   * Layout reads only. Called for every queued ticker before any DOM writes.
   * @returns {{ instance: HorizontalTextScroll, originals: HTMLElement[], setWidth: number, viewportWidth: number, track: HTMLElement } | null}
   */
  #collectFillPlan() {
    const { track } = this.refs;
    if (!track) return null;

    const originals = this.#getOriginals();
    if (originals.length === 0) return null;

    const viewport = this.refs.viewport;
    const viewportWidth =
      this.#viewportWidthHint ||
      (viewport instanceof HTMLElement ? viewport.clientWidth : this.clientWidth);

    if (viewportWidth < 2) {
      if (this.#widthRetries < 12) {
        this.#widthRetries += 1;
        requestAnimationFrame(() => this.#scheduleFill());
      }
      return null;
    }

    const setWidth = this.#measureSetWidth(originals);
    if (setWidth <= 0) return null;

    this.#widthRetries = 0;
    return { instance: this, originals, setWidth, viewportWidth, track };
  }

  /**
   * @param {{ originals: HTMLElement[], setWidth: number, viewportWidth: number, track: HTMLElement }} plan
   */
  #applyFillPlan(plan) {
    if (this.#building) return;

    const { originals, setWidth, viewportWidth, track } = plan;
    this.#building = true;

    track.querySelectorAll('[data-hts-clone]').forEach((clone) => clone.remove());

    const appendSet = () => {
      const fragment = document.createDocumentFragment();
      originals.forEach((item) => {
        fragment.appendChild(this.#cloneItem(item));
      });
      track.appendChild(fragment);
    };

    const extraSets = Math.min(40, Math.max(1, Math.ceil(viewportWidth / setWidth)));
    for (let i = 0; i < extraSets; i++) {
      appendSet();
    }

    this.style.setProperty('--hts-total-width', `${setWidth}px`);

    if (Math.abs(setWidth - this.#lastSetWidth) > 0.5) {
      this.#lastSetWidth = setWidth;
      this.#restartAnimation();
    }

    this.#mutationObserver?.takeRecords();
    this.#building = false;
  }
}

if (!customElements.get('horizontal-text-scroll')) {
  customElements.define('horizontal-text-scroll', HorizontalTextScroll);
}
